import { Fragment, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';

import { getBankList } from 'state/reducers/global.reducer';
import FormCreatorComponent, { isValidForm } from 'components/FormCreatorComponent';
import { createFormProps, fieldType } from 'components/FormCreatorComponent/types';
import {
	FormCard,
	FormCard1ColumnLayout,
	FormCard2ColumnLayout,
	FormCardLayout,
	FormCardSingleColumnLayout,
} from 'theme/elements/Forms';

import { Box, Stack } from '@mui/material';
import { ButtonComponent } from 'components/ButtonComponent';
import {
	getActiveStepIdx,
	getIsDirty,
	getNewAccount,
	getNewBankAccount,
	getNewBankAccounts,
	initialNewBankAccounts,
	setIsDirty,
	setNewBankAccount,
	setNewBankAccounts,
	getIsLoading,
} from 'state/reducers/new_accounts.reducers';
import {
	approveBankAccountDetails,
	deleteNewBankAccount,
	sendToReview,
	upsertBankAccount,
} from 'services/new_accounts.service';
import {
	NewAccountStatus,
	NewBankAccountStatus,
	continueOptions,
	newAccountsSteps,
} from 'constants/new_accounts.constants';
import { newAccountTypesForms } from 'sections/NewAccounts/forms.types';
import { overrideNewAccountFormsToApprove } from 'sections/NewAccounts/forms.types.approve';
import { overrideNewAccountFormsToCreate } from 'sections/NewAccounts/forms.types.create';
import { useParams } from 'react-router-dom';
import { FadeIn } from 'theme/elements/Content';
import { prepareBankAccountInitialValues } from '../Stepper/helper';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';
import { setError } from 'state/reducers/pending-requests.reducer';

export interface IBankAccountsDetails {
	handleBack: any;
	isApproveMode?: boolean;
	handleForward?: any;
}

export const BankAccountDetails = ({ handleBack, isApproveMode, handleForward }: IBankAccountsDetails) => {
	const t = useAppSelector(selectTranslations);
	const dispatch = useAppDispatch();
	const { action } = useParams();
	const newAccount = useAppSelector(getNewAccount);
	const newBankAccounts = useAppSelector(getNewBankAccounts);
	const newBankAccount = useAppSelector(getNewBankAccount);
	const activeStepIdx = useAppSelector(getActiveStepIdx);
	const isLoading = useAppSelector(getIsLoading);
	const isDirty = useAppSelector(getIsDirty);
	const [initialValues, setInitialValues] = useState<Record<string, any>>({});
	const bankList = useAppSelector(getBankList);
	const [forms, setForms] = useState<createFormProps[]>();
	const [formState, setFormState] = useState<Record<string, any>>({});
	const initialNewBankAccount = {
		id: 'new',
		accountOwnerName: `*${t('page.accounts.new_account.user_details.bankAccount')}`,
	};
	const upsertBankAccountPermission = hasPermission([permissionEnums.UPSERT_BANK_ACCOUNT]);
	const approveBankAccountPermission = hasPermission([permissionEnums.APPROVE_BANK_ACCOUNT]);
	const sendToReviewPermission = hasPermission([permissionEnums.UPSERT_ACCOUNT]);

	const optionsDependies = useMemo(() => {
		const options: Record<string, any> = {};
		if (bankList) {
			_.assign(options, {
				bankId: bankList.map((bank: any) => {
					return { value: bank.id, label: _.trim(bank.bankName, ' ') };
				}),
			});
		}
		return options;
	}, [bankList]);

	const handleSaveBankAccount = (continueOption: continueOptions) => {
		if (upsertBankAccountPermission) {
			let bankAccountData = { ...initialValues };
			_.values(formState).forEach(
				(prop) =>
					(bankAccountData =
						prop?.dirtyFields && !_.isEmpty(prop.dirtyFields)
							? { ...bankAccountData, ...prop.dirtyFields }
							: bankAccountData)
			);
			if (_.has(newBankAccount, 'id') && _.get(newBankAccount, 'id') !== 'new') {
				_.assign(bankAccountData, { id: newBankAccount.id });
			}
			//check duplicate email
			if (
				_.find(
					_.values(newBankAccounts),
					(user) =>
						_.isEqual(user.accountNumber, bankAccountData.accountNumber) &&
						!_.isEqual(user.id, bankAccountData?.id)
				)
			) {
				dispatch(
					setError({
						exist: true,
						text: _.replace(t('validations.errors.alreadyExist'), '{0}', t('label.accountNumber')),
					})
				);
				return false;
			}

			dispatch(
				upsertBankAccount(
					{
						newAccountId: newAccount.id,
						bankAccountData,
						file: bankAccountData.file,
					},
					continueOption
				)
			);
		}
	};

	useEffect(() => {
		const values = prepareBankAccountInitialValues(newBankAccount);
		if (action !== 'review') {
			setForms([]);
		}
		setInitialValues(values);
		if (newBankAccount?.status === NewBankAccountStatus.DRAFT && isApproveMode && approveBankAccountPermission) {
			dispatch(approveBankAccountDetails(newBankAccount.id, newAccount.id));
		}
		window.scrollTo(0, 0);
	}, [newBankAccount]);

	useEffect(() => {
		if (_.isEmpty(newBankAccounts)) {
			if (activeStepIdx === 3) {
				dispatch(setNewBankAccounts({ new: initialNewBankAccount }));
				dispatch(setNewBankAccount(initialNewBankAccount));
			}
		} else {
			if (_.isEmpty(newBankAccount)) {
				const indexBank = isApproveMode ? 0 : _.values(newBankAccounts).length - 1;
				dispatch(setNewBankAccount(_.values(newBankAccounts)[indexBank]));
			}
		}
	}, [newBankAccounts]);

	useEffect(() => {
		if (initialValues) {
			const typeForms = _.get(newAccountTypesForms, newAccountsSteps.BANK_ACCOUNT_DETAILS);
			let formsState: Record<string, any> = {};
			typeForms.forEach((form) => {
				const dirtyValues =
					_.has(formState, form.name) && _.has(formState[form.name], 'dirtyFields')
						? _.get(formState[form.name], 'dirtyFields')
						: {};
				formsState = {
					...formsState,
					[form.name]: isValidForm(form.fieldsGroups, { ...initialValues, ...dirtyValues }),
				};
			});
			setFormState(formsState);
		}
	}, [initialValues]);

	useEffect(() => {
		if (newBankAccount.id !== 'new') {
			dispatch(
				setIsDirty({
					dirty: _.some(_.values(formState), { isDirty: true }),
					type: newAccountsSteps.BANK_ACCOUNT_DETAILS,
				})
			);
		}
	}, [formState]);

	useEffect(() => {
		const basicForms = _.get(newAccountTypesForms, newAccountsSteps.BANK_ACCOUNT_DETAILS);
		const override = isApproveMode
			? _.get(overrideNewAccountFormsToApprove, newAccountsSteps.BANK_ACCOUNT_DETAILS)
			: _.get(overrideNewAccountFormsToCreate, newAccountsSteps.BANK_ACCOUNT_DETAILS);
		const final = isApproveMode ? _.merge({}, override) : _.merge({}, basicForms, override);
		const formsArr = _.map(final, (form) => ({
			...form,
			fieldsGroups: _.map(form.fieldsGroups, (group) => ({
				...group,
				fields: _.map(group.fields, (field) => ({
					...field,
					props: {
						...field.props,
						options:
							_.isEqual(field.type, fieldType.select) && _.has(optionsDependies, field.props.name)
								? _.get(optionsDependies, field.props.name)
								: field.props?.options || [],
					},
				})),
			})),
		}));
		setForms([...formsArr]);
	}, [initialValues, optionsDependies]);

	const CardLayout = action === 'review' ? FormCardLayout : FormCardSingleColumnLayout;
	const ButtonsLayout = action === 'review' ? FormCard2ColumnLayout : FormCard1ColumnLayout;
	return (
		<Fragment>
			<FadeIn>
				<CardLayout>
					{forms &&
						forms.map((form: any, index: number) => {
							return (
								<FormCard key={index} title={t(form.name)}>
									<FormCreatorComponent
										{...form}
										translation={t}
										checkInitValidation={false}
										values={initialValues}
										handleChange={(newFormState: Record<string, any>) => {
											setFormState({ ...formState, [form.name]: newFormState });
										}}
									/>
								</FormCard>
							);
						})}
				</CardLayout>
				<ButtonsLayout>
					<ButtonComponent
						disabled={isLoading || isDirty.dirty}
						handleClick={handleBack}
						text={`<${t('label.back')}`}
						className="buttonForm"
					/>
					{action !== 'review' && (
						<ButtonComponent
							disabled={isLoading || !isDirty.dirty}
							handleClick={() => {
								if (isDirty.dirty || _.some(_.values(formState), { isDirty: true })) {
									setForms([]);
									if (newBankAccount.id === 'new') {
										dispatch(initialNewBankAccounts(_.omit(newBankAccounts, 'new')));
										if (!_.isEmpty(_.omit(newBankAccount, 'new'))) {
											dispatch(
												setNewBankAccount(_.last(_.values(_.omit(newBankAccount, 'new'))))
											);
										}
									} else {
										const values = prepareBankAccountInitialValues(newBankAccount);
										setInitialValues(values);
										dispatch(
											setIsDirty({
												dirty: false,
											})
										);
									}
								}
							}}
							text={`${newBankAccount.id === 'new' ? t('button.cancel') : t('button.cancel_changes')}`}
							className="buttonForm"
						/>
					)}
					{action !== 'review' && newBankAccount.id !== 'new' && _.keys(newBankAccounts).length > 1 && (
						<ButtonComponent
							disabled={isLoading}
							handleClick={() => {
								dispatch(deleteNewBankAccount({ id: newBankAccount.id }));
							}}
							text={`${t('button.delete')}`}
							className="buttonForm"
						/>
					)}
					{action !== 'review' && (
						<ButtonComponent
							disabled={isLoading || !_.every(_.values(formState), { isValid: true })}
							handleClick={() => {
								handleSaveBankAccount(continueOptions.SAVE_AND_NEW);
							}}
							text={`${t('label.save_and_add')}`}
							icon={
								<img
									src={'/icons/plus-sign.svg'}
									style={{
										marginLeft: '10px',
									}}
								/>
							}
							className="buttonForm"
						/>
					)}
					<Stack gap={2} flexDirection={'row'}>
						{isApproveMode && _.last(_.keys(newBankAccounts)) !== _.toString(newBankAccount?.id) && (
							<ButtonComponent
								disabled={isLoading}
								handleClick={() => {
									const nextId =
										_.indexOf(_.keys(newBankAccounts), _.toString(newBankAccount?.id)) + 1;
									dispatch(setNewBankAccount({ ..._.values(newBankAccounts)[nextId] }));
								}}
								text={`${t('label.to_reviewerBankAccount')}>`}
								className="buttonForm"
							/>
						)}
						<ButtonComponent
							disabled={
								isLoading || isApproveMode
									? !_.every(_.values(newBankAccounts), { status: NewBankAccountStatus.APPROVED })
									: !_.every(_.values(formState), { isValid: true })
							}
							handleClick={() => {
								if (_.some(_.values(formState), { isDirty: true })) {
									isApproveMode
										? handleForward()
										: handleSaveBankAccount(continueOptions.SAVE_AND_CONTINUE);
								} else {
									isApproveMode
										? approveBankAccountPermission
											? handleForward()
											: undefined
										: sendToReviewPermission
										? dispatch(sendToReview(newAccount.id))
										: undefined;
								}
							}}
							text={`${t('label.to_reviewer')}>`}
							className="buttonForm"
						/>
					</Stack>
				</ButtonsLayout>
			</FadeIn>
		</Fragment>
	);
};
